import React, { useContext, useEffect, useState } from 'react';
import Isvg from 'react-inlinesvg';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { createErrorBanner } from 'components/Banner';
import Button from 'components/Button';
import { Card } from 'components/Card';
import { Dropdown, DropdownItem, DropdownMenu } from 'components/Dropdown2';
import FeatureNotSupported from 'components/FeatureNotSupported';
import { FlyoutContext } from 'components/Flyout/FlyoutContext';
import LoadingDots from 'components/LoadingDots';
import Message from 'components/Message';
import { createErrorSnackbar, createSuccessSnackbar } from 'components/Snackbar';
import When from 'components/When';
import { ICONS, ILLUSTRATIONS } from 'constants/icons';
import LINKS from 'constants/links';
import { Title } from 'features/App';
import { expandSection } from 'features/shared/collapseStorage';
import { bem } from 'helpers/class-name';
import { reportError } from 'helpers/error-logging';
import { IA_KEY } from 'helpers/ia-logic';
import { makePath } from 'helpers/link-generation';
import segment from 'helpers/segment';
import { selectActiveVenue, selectIaLogic } from 'selectors/general';

import { ADD_ITEM_FLYOUT_ID_PREFIX, getItemsCollapseKey, ITEM_AVAILABILITY_STATUS } from './Items.constants';
import {
  ITEM_CATEGORY_UPDATED_EVENT,
  makeItemAvailable,
  makeItemUnavailableIndefinitely,
  makeItemUnavailableToday,
  getTransformedItems,
  itemCategoryUpdatedEventTarget,
} from './Items.helpers';
import { itemsMessages as messages, settingsMessages } from './Items.messages';
import { generateFlyoutId } from './Items.utils';
import ItemsGroup from './components/ItemsGroup';
import { ItemFormContext } from './components/ItemFormContext';
import VenueOpenStatus from './components/VenueOpenStatus';

import './Items.scss';
import ItemForm from './components/ItemForm';

// eslint-disable-next-line react/prop-types
const DropdownTrigger = ({ toggleMenu, closeMenu, isOpen, isAutocomplete, disabled, id, ...rest }) => (
  <Button {...rest} onClick={toggleMenu} />
);

const getAvailabilityStatusResponseMessages = status => {
  // eslint-disable-next-line default-case
  switch (status) {
    case ITEM_AVAILABILITY_STATUS.AVAILABLE:
      return {
        successMessage: messages.itemAvailabileUpdateSuccess,
        errorMessageTitle: messages.itemAvailableUpdateErrorTitle,
      };

    case ITEM_AVAILABILITY_STATUS.UNAVAILABLE_TODAY:
    case ITEM_AVAILABILITY_STATUS.UNAVAILABLE_INDEFINITELY:
      return {
        successMessage: messages.itemUnavailabileUpdateSuccess,
        errorMessageTitle: messages.itemUnavailableUpdateErrorTitle,
      };
  }
};

const { block, element } = bem('items');

const Items = () => {
  const dispatch = useDispatch();
  const { setCurrentItem } = useContext(ItemFormContext);
  const { openFlyout } = useContext(FlyoutContext);
  const { venueId, timezone: venueTimezone } = useSelector(selectActiveVenue);
  const { featureIsSupported } = useSelector(state => selectIaLogic(state, IA_KEY.ITEM_MANAGEMENT, venueId));

  const [isLoading, setIsLoading] = useState(null);
  const [itemsByCategory, setItemsByCategory] = useState([]);
  const [itemsList, setItemsList] = useState([]);

  const fetchAndSetItems = async () => {
    setIsLoading(true);
    try {
      const { flatItems, groupedItems } = await getTransformedItems(venueId, venueTimezone);
      setItemsByCategory(groupedItems);
      setItemsList(flatItems);
    } catch (error) {
      reportError('Error fetching Items data', error);
      dispatch(createErrorBanner(messages.itemsLoadError));
    }
    setIsLoading(false);
  };

  const onChangeAvailabilityStatus = item => async status => {
    const { availabilityStatus, classId } = item;
    if (status === availabilityStatus) {
      return;
    }

    setIsLoading(true);

    const { successMessage, errorMessageTitle } = getAvailabilityStatusResponseMessages(status);
    try {
      // eslint-disable-next-line default-case
      switch (status) {
        case ITEM_AVAILABILITY_STATUS.AVAILABLE:
          await makeItemAvailable(venueId, item);
          segment.event('Click Item Available', { venueId, classId });
          break;

        case ITEM_AVAILABILITY_STATUS.UNAVAILABLE_TODAY:
          await makeItemUnavailableToday(venueId, venueTimezone, item);
          segment.event('Click Item Unavailable today', { venueId, classId });
          break;

        case ITEM_AVAILABILITY_STATUS.UNAVAILABLE_INDEFINITELY:
          await makeItemUnavailableIndefinitely(venueId, venueTimezone, item);
          segment.event('Click Item Unavailable indefinitely', { venueId, classId });
          break;
      }

      dispatch(
        createSuccessSnackbar(
          successMessage,
          {
            itemName: item?.name,
            strong: chunks => <strong>{chunks}</strong>,
          },
          { isLowStyle: true }
        )
      );
    } catch (error) {
      reportError('Error updating item availability status', error);
      dispatch(
        createErrorSnackbar(
          errorMessageTitle,
          { strong: chunks => <strong>{chunks}</strong> },
          { isLowStyle: true },
          {
            ...messages.itemAvailabilityUpdateErrorDescription,
            values: {
              'contact-us-link': chunks => (
                <a
                  href={LINKS.contact.partnerSuccessHref}
                  target="_blank"
                  rel="noreferrer noopener"
                  className={element('snackbar-link')}>
                  {chunks}
                </a>
              ),
            },
          }
        )
      );
    }

    fetchAndSetItems();
  };

  const onFormSuccess = () => {
    const storageKey = getItemsCollapseKey(venueId);
    itemsByCategory.forEach(({ category }) => expandSection(storageKey, category));
    fetchAndSetItems();
  };

  useEffect(() => {
    if (featureIsSupported) {
      fetchAndSetItems();
    }
  }, [featureIsSupported, venueId]);

  useEffect(() => {
    itemCategoryUpdatedEventTarget.addEventListener(ITEM_CATEGORY_UPDATED_EVENT, fetchAndSetItems);

    return () => {
      itemCategoryUpdatedEventTarget.removeEventListener(ITEM_CATEGORY_UPDATED_EVENT, fetchAndSetItems);
    };
  }, []);

  const addItem = () => {
    setCurrentItem({});
    const addItemFlyoutId = generateFlyoutId(ADD_ITEM_FLYOUT_ID_PREFIX);
    openFlyout(addItemFlyoutId);
    segment.event('Click Add Item', { venueId });
  };
  const navigate = useNavigate();

  const handleAddVenueClosedDates = () => {
    navigate(makePath.editVenueProfile(venueId));
    segment.event('Click Add closed Dates', { venueId });
  };

  const handleEditVenueProfile = () => {
    navigate(makePath.editVenuePhotos(venueId));
    segment.event('Click Business Profile', { venueId });
  };

  if (!featureIsSupported) {
    return (
      <>
        <Message message={messages.title} as={Title} />
        <FeatureNotSupported />
      </>
    );
  }

  return (
    <div className={block()}>
      <Message message={messages.title} as={Title} />
      <div className={element('settings-bar')}>
        <div className={element('venue-status-container')}>
          <VenueOpenStatus />
        </div>
        <div className={element('settings-buttons-container')}>
          <Message as={Button} message={messages.addItem} styled="tertiary" onClick={addItem} />
          <Dropdown attachment="right" isSmallRadiusStyle>
            {({ toggleMenu }) => (
              <>
                <DropdownTrigger className={element('settings-button-icon')} styled="tertiary" onClick={toggleMenu}>
                  <Isvg src={ICONS.cog} />
                </DropdownTrigger>
                <DropdownMenu>
                  <DropdownItem onClick={handleAddVenueClosedDates}>
                    <Message message={settingsMessages.closedDates} />
                  </DropdownItem>
                  <DropdownItem onClick={handleEditVenueProfile}>
                    <Message message={settingsMessages.businessProfile} />
                  </DropdownItem>
                </DropdownMenu>
              </>
            )}
          </Dropdown>
        </div>
      </div>
      <When predicate={isLoading}>
        <LoadingDots />
      </When>
      <When predicate={!isLoading}>
        <When predicate={itemsByCategory.length > 0}>
          <div className={element('items-group-list')}>
            {itemsByCategory.map(({ category, items }) => (
              <ItemsGroup
                key={category}
                className={element('items-group')}
                category={category}
                items={items}
                collapseStorageKey={getItemsCollapseKey(venueId)}
                onAddItem={addItem}
                onChangeAvailabilityStatus={onChangeAvailabilityStatus}
              />
            ))}
          </div>
        </When>
        <When predicate={itemsByCategory.length === 0}>
          <Card className={element('stretch-card', 'card--no-border')}>
            <div className={element('center-content')}>
              <Isvg src={ILLUSTRATIONS.itemTag} />
              <Message className={element('items-will-appear-text')} as="div" message={messages.itemsWillAppear} />
              <Message className={element('add-item-text')} as="div" message={messages.addItemToGetStarted} />
              <Message
                className={element('add-item-button')}
                as={Button}
                message={messages.addItem}
                styled="primary"
                onClick={addItem}
              />
            </div>
          </Card>
        </When>
      </When>
      <ItemForm items={itemsList} onSuccess={onFormSuccess} />
    </div>
  );
};

export default Items;
